/* ==========================================================================
   STRANGE MATTER — global behaviour
   Vanilla, deferred, dependency free. Exposes window.SMTheme.

   Public API
     SMTheme.addToCart(variantId, quantity) -> Promise
     SMTheme.changeLine(line, quantity)     -> Promise   (line is 1 based)
     SMTheme.openCart() / closeCart() / toggleCart()
     SMTheme.openMenu() / closeMenu()
     SMTheme.reloadDrawer()                 -> Promise

   Markup contract (data attributes)
     [data-sm-drawer]              cart drawer panel      (id required)
     [data-sm-cart-drawer-inner]   re-rendered wrapper
     [data-sm-cart-error]          aria-live error region
     [data-sm-menu-drawer]         mobile menu panel      (id required)
     [data-sm-overlay]             scrim behind any drawer
     [data-sm-cart-toggle] [data-sm-cart-close]
     [data-sm-menu-toggle] [data-sm-menu-close]
     [data-sm-add-to-cart] + data-variant-id / data-quantity / data-sm-demo
     [data-sm-line-change] + data-line / data-quantity
     [data-sm-line-remove] + data-line
     [data-sm-qty] > [data-sm-qty-down] [data-sm-qty-input] [data-sm-qty-up]
     [data-sm-cart-count]          every cart counter on the page
     [data-sm-sticky-header]       fixed bar toggled by .is-visible
     [data-sm-hero]                hero root observed for the sticky bar
     [data-sm-art-break] + [data-sm-art-break-img] + data-zoom-from
   ========================================================================== */
(function () {
  'use strict';

  var R = window.SMRoutes || {};
  var URL_ADD = R.cartAdd || '/cart/add.js';
  var URL_CHANGE = R.cartChange || '/cart/change.js';
  var URL_CART = R.cart || '/cart';
  var ROOT = R.root || '/';
  var SECTION = 'sm-cart-drawer';

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"])';

  var lastFocus = null;
  var activeDrawer = null;

  /* ---------------------------------------------------------------- utils */

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function prefersReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
    } catch (e) { /* no-op */ }
  }

  function visibleFocusables(root) {
    return qsa(FOCUSABLE, root).filter(function (el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  function setExpanded(panel, state) {
    if (!panel || !panel.id) return;
    qsa('[aria-controls="' + panel.id + '"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', state ? 'true' : 'false');
    });
  }

  function setBusy(el, state) {
    if (!el) return;
    if (state) {
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-busy');
    } else {
      el.removeAttribute('aria-busy');
      el.classList.remove('is-busy');
    }
  }

  /* --------------------------------------------------------------- drawers */

  function cartDrawer() { return qs('[data-sm-drawer]'); }
  function menuDrawer() { return qs('[data-sm-menu-drawer]'); }
  function overlays() { return qsa('[data-sm-overlay]'); }

  function anyOpen() {
    return !!(qs('[data-sm-drawer].is-open') || qs('[data-sm-menu-drawer].is-open'));
  }

  /* Freeze the page behind an open drawer — but only where taking the
     scrollbar away costs nothing. A classic scrollbar (desktop) would hand
     its gutter back to the layout the moment overflow is hidden, widening
     the page and jerking every fixed bar sideways, so those viewports keep
     scrolling instead. Phones, tablets and overlay-scrollbar desktops all
     report a zero gutter and get the lock. */
  function setScrollLock(state) {
    var root = document.documentElement;
    if (!state) {
      root.classList.remove('sm-scroll-locked');
      return;
    }
    if (window.innerWidth - root.clientWidth > 0) return;
    root.classList.add('sm-scroll-locked');
  }

  function openDrawer(panel) {
    if (!panel || panel.classList.contains('is-open')) return;
    var opener = document.activeElement;

    // Only one drawer at a time; closing the other must not steal the opener.
    var other = panel === cartDrawer() ? menuDrawer() : cartDrawer();
    if (other && other.classList.contains('is-open')) closeDrawer(other, false);

    lastFocus = opener && opener !== document.body ? opener : null;
    panel.classList.add('is-open');
    overlays().forEach(function (o) { o.classList.add('is-open'); });
    setExpanded(panel, true);
    setScrollLock(true);
    activeDrawer = panel;

    window.setTimeout(function () {
      if (!panel.classList.contains('is-open')) return;
      var first = visibleFocusables(panel)[0];
      try { (first || panel).focus(); } catch (e) { /* no-op */ }
    }, 60);
  }

  function closeDrawer(panel, restoreFocus) {
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    setExpanded(panel, false);
    if (activeDrawer === panel) activeDrawer = null;

    if (!anyOpen()) {
      overlays().forEach(function (o) { o.classList.remove('is-open'); });
      setScrollLock(false);
    }

    if (restoreFocus !== false && lastFocus && document.contains(lastFocus)) {
      try { lastFocus.focus(); } catch (e) { /* no-op */ }
    }
    if (restoreFocus !== false) lastFocus = null;
  }

  function openCart() { openDrawer(cartDrawer()); }
  function closeCart() { closeDrawer(cartDrawer(), true); }

  function toggleCart() {
    var panel = cartDrawer();
    if (!panel) return;
    if (panel.classList.contains('is-open')) closeCart();
    else openCart();
  }

  function openMenu() { openDrawer(menuDrawer()); }
  function closeMenu() { closeDrawer(menuDrawer(), true); }

  function closeAll() {
    closeDrawer(cartDrawer(), true);
    closeDrawer(menuDrawer(), true);
  }

  /* Esc to close + focus trap inside the open drawer. */
  document.addEventListener('keydown', function (e) {
    var key = e.key;

    if (key === 'Escape' || key === 'Esc' || e.keyCode === 27) {
      if (activeDrawer) {
        e.preventDefault();
        closeDrawer(activeDrawer, true);
      }
      return;
    }

    if (key !== 'Tab' && e.keyCode !== 9) return;
    if (!activeDrawer) return;

    var list = visibleFocusables(activeDrawer);
    if (!list.length) {
      e.preventDefault();
      try { activeDrawer.focus(); } catch (err) { /* no-op */ }
      return;
    }

    var first = list[0];
    var last = list[list.length - 1];
    var current = document.activeElement;

    if (!activeDrawer.contains(current)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ cart */

  function setError(message) {
    qsa('[data-sm-cart-error]').forEach(function (el) {
      el.textContent = message || '';
      if (message) el.setAttribute('data-sm-has-error', 'true');
      else el.removeAttribute('data-sm-has-error');
    });
  }

  function setCounts(count) {
    var value = String(count);
    qsa('[data-sm-cart-count]').forEach(function (el) {
      el.textContent = value;
    });
  }

  function readDrawerCount() {
    var inner = qs('[data-sm-cart-drawer-inner]');
    var node = inner && qs('[data-sm-cart-count]', inner);
    if (!node) return null;
    var n = parseInt(node.textContent.replace(/[^0-9-]/g, ''), 10);
    return isNaN(n) ? null : n;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = JSON.parse(text); } catch (e) { data = null; }
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || 'Something went wrong. Please try again.');
          err.data = data;
          throw err;
        }
        return data || {};
      });
    });
  }

  /* Swap the freshly rendered drawer body in. Returns true when applied. */
  function applySections(sections) {
    var html = sections && sections[SECTION];
    if (!html) return false;
    var inner = qs('[data-sm-cart-drawer-inner]');
    if (!inner) return false;
    var parsed;
    try {
      parsed = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return false;
    }
    var fresh = parsed.querySelector('[data-sm-cart-drawer-inner]');
    if (!fresh) return false;
    inner.innerHTML = fresh.innerHTML;
    return true;
  }

  function reloadDrawer() {
    var url = ROOT + (ROOT.indexOf('?') > -1 ? '&' : '?') + 'sections=' + SECTION;
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        applySections(data);
        var c = readDrawerCount();
        if (c !== null) setCounts(c);
        return data;
      })
      .catch(function () { /* leave the drawer as-is */ });
  }

  function fetchCart() {
    return fetch(URL_CART + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  function afterMutation(data) {
    var applied = applySections(data && data.sections);
    var count = data && typeof data.item_count === 'number' ? data.item_count : null;

    if (count === null && applied) count = readDrawerCount();

    if (count !== null) setCounts(count);

    if (!applied) {
      // Section rendering unavailable — rebuild from the storefront instead.
      reloadDrawer();
      if (count === null) {
        fetchCart().then(function (cart) {
          if (cart && typeof cart.item_count === 'number') setCounts(cart.item_count);
        }).catch(function () { /* no-op */ });
      }
    }

    emit('sm:cart:updated', { data: data });
  }

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  function addToCart(variantId, quantity) {
    var id = variantId;
    var qty = parseInt(quantity, 10);
    if (!qty || qty < 1) qty = 1;

    if (!id) return Promise.reject(new Error('Missing variant id'));

    setError('');
    return postJSON(URL_ADD, {
      items: [{ id: id, quantity: qty }],
      sections: SECTION,
      sections_url: sectionsUrl()
    }).then(function (data) {
      afterMutation(data);
      openCart();
      return data;
    }).catch(function (err) {
      setError(err.message);
      openCart();
      throw err;
    });
  }

  function changeLine(line, quantity) {
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 0) qty = 0;

    setError('');
    return postJSON(URL_CHANGE, {
      line: parseInt(line, 10),
      quantity: qty,
      sections: SECTION,
      sections_url: sectionsUrl()
    }).then(function (data) {
      afterMutation(data);
      return data;
    }).catch(function (err) {
      setError(err.message);
      throw err;
    });
  }

  /* --------------------------------------------------------- qty steppers */

  function stepQuantity(btn) {
    var wrap = btn.closest('[data-sm-qty]');
    if (!wrap) return;
    var input = qs('[data-sm-qty-input]', wrap);
    if (!input) return;

    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = 1;
    value += btn.hasAttribute('data-sm-qty-up') ? 1 : -1;
    if (value < 1) value = 1;
    if (value > 99) value = 99;

    if (String(value) === String(input.value)) return;
    input.value = value;
    try {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------ delegated click router */

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var el;

    el = target.closest('[data-sm-cart-close]');
    if (el) { e.preventDefault(); closeCart(); return; }

    el = target.closest('[data-sm-menu-close]');
    if (el) { e.preventDefault(); closeMenu(); return; }

    el = target.closest('[data-sm-cart-toggle]');
    if (el) {
      if (!cartDrawer()) return; // no drawer on the page: let the link work
      e.preventDefault();
      toggleCart();
      return;
    }

    el = target.closest('[data-sm-menu-toggle]');
    if (el) {
      if (!menuDrawer()) return;
      e.preventDefault();
      openMenu();
      return;
    }

    if (target.closest('[data-sm-overlay]')) { closeAll(); return; }

    el = target.closest('[data-sm-line-remove]');
    if (el) {
      e.preventDefault();
      setBusy(el, true);
      changeLine(el.getAttribute('data-line'), 0)['catch'](function () { /* surfaced in drawer */ })
        .then(function () { setBusy(el, false); });
      return;
    }

    el = target.closest('[data-sm-line-change]');
    if (el) {
      e.preventDefault();
      setBusy(el, true);
      changeLine(el.getAttribute('data-line'), el.getAttribute('data-quantity'))['catch'](function () { /* surfaced in drawer */ })
        .then(function () { setBusy(el, false); });
      return;
    }

    el = target.closest('[data-sm-qty-up], [data-sm-qty-down]');
    if (el) { e.preventDefault(); stepQuantity(el); return; }

    el = target.closest('[data-sm-add-to-cart]');
    if (el) {
      e.preventDefault();

      if (el.hasAttribute('data-sm-demo')) { openCart(); return; }

      // A featured-product ATC owns its own submit path (section JS reads the
      // selected variant + quantity); never add twice for the same click.
      if (el.hasAttribute('data-sm-atc')) return;

      var variantId = el.getAttribute('data-variant-id');
      if (!variantId) { openCart(); return; }

      setBusy(el, true);
      addToCart(variantId, el.getAttribute('data-quantity') || 1)['catch'](function () { /* surfaced in drawer */ })
        .then(function () { setBusy(el, false); });
      return;
    }
  });

  /* -------------------------------------------------------- sticky header */

  var stickyObserver = null;
  var stickyScroll = null;

  function initStickyHeader() {
    // Always tear the previous binding down first: the theme editor swaps
    // whole sections out from under us.
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
    if (stickyScroll) {
      window.removeEventListener('scroll', stickyScroll);
      stickyScroll = null;
    }

    var bar = qs('[data-sm-sticky-header]');
    if (!bar) return;

    var root = document.documentElement;
    var hero = qs('[data-sm-hero]');

    if (!hero) {
      root.classList.add('sm-no-hero');
      stickyScroll = function () {
        bar.classList.toggle('is-visible', window.pageYOffset > 40);
      };
      window.addEventListener('scroll', stickyScroll, { passive: true });
      stickyScroll();
      return;
    }

    root.classList.remove('sm-no-hero');

    if (!('IntersectionObserver' in window)) {
      stickyScroll = function () {
        bar.classList.toggle('is-visible', window.pageYOffset > window.innerHeight * 0.9);
      };
      window.addEventListener('scroll', stickyScroll, { passive: true });
      stickyScroll();
      return;
    }

    // -10% top margin makes the bar appear at ~90vh for a 100vh hero,
    // matching the prototype's scrollY > 90vh threshold.
    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { root: null, rootMargin: '-10% 0px 0px 0px', threshold: 0 });

    stickyObserver.observe(hero);
  }

  /* ------------------------------------------------------------ art break */

  var artItems = [];
  var artBound = false;
  var artTicking = false;

  function artUpdate() {
    artTicking = false;
    var vh = window.innerHeight;
    for (var i = 0; i < artItems.length; i++) {
      var item = artItems[i];
      var rect = item.sec.getBoundingClientRect();
      var span = rect.height - vh;
      var p = span > 0 ? Math.min(1, Math.max(0, -rect.top / span)) : 1;
      var scale = item.from - (item.from - 1) * p;
      item.img.style.transform = 'scale(' + scale.toFixed(4) + ')';
    }
  }

  function artOnScroll() {
    if (artTicking) return;
    artTicking = true;
    window.requestAnimationFrame(artUpdate);
  }

  function initArtBreak() {
    artItems = [];

    qsa('[data-sm-art-break]').forEach(function (sec) {
      var img = qs('[data-sm-art-break-img]', sec);
      if (!img) return;
      var from = parseFloat(sec.getAttribute('data-zoom-from'));
      if (!from || from < 1) from = 1.6;
      artItems.push({ sec: sec, img: img, from: from });
    });

    if (!artItems.length) return;

    if (prefersReduced()) {
      // Motion is off: rest the artwork at its natural scale and never
      // attach the scroll handler.
      artItems.forEach(function (item) { item.img.style.transform = 'scale(1)'; });
      artItems = [];
      return;
    }

    if (!artBound) {
      artBound = true;
      window.addEventListener('scroll', artOnScroll, { passive: true });
      window.addEventListener('resize', artOnScroll, { passive: true });
    }

    artUpdate();
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    initStickyHeader();
    initArtBreak();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections are re-injected without a page load.
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-sm-drawer]')) openCart();
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-sm-drawer]')) closeCart();
  });

  window.SMTheme = {
    addToCart: addToCart,
    changeLine: changeLine,
    openCart: openCart,
    closeCart: closeCart,
    toggleCart: toggleCart,
    openMenu: openMenu,
    closeMenu: closeMenu,
    closeAll: closeAll,
    reloadDrawer: reloadDrawer,
    setCartError: setError,
    routes: R
  };
}());
